import { styled } from '@mui/material/styles';
import { PickersDay } from '@mui/x-date-pickers';
import { DATE_UNAVAILABILITY_REASON } from '../../../../utils/availabilityStatus';

const DAY_FONT_COLOR = {
  available: 'rgba(0, 0, 0, 0.87)',
  restricted: 'rgba(0, 0, 0, 0.45)',
  outOfRange: 'rgba(0, 0, 0, 0.32)',
  noInventory: 'rgba(0, 0, 0, 0.26)',
};

const RESTRICTED_REASON_CLASS_SELECTOR = [
  DATE_UNAVAILABILITY_REASON.RATE_PLAN_UNAVAILABLE,
  DATE_UNAVAILABILITY_REASON.CHECKIN_DAY_NOT_ALLOWED,
  DATE_UNAVAILABILITY_REASON.CHECKOUT_DAY_NOT_ALLOWED,
  DATE_UNAVAILABILITY_REASON.MIN_LOS_NOT_MET,
  DATE_UNAVAILABILITY_REASON.NO_VALID_STAY,
]
  .map((reason) => `&.disabledReason-${reason}:not(.selected)`)
  .join(', ');

/** Shared by every day cell. Defined once -> one generated CSS class, state driven by class names. */
export const CalendarDayRoot = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  overflow: 'hidden',
  borderRadius: 0,
  backgroundColor: 'transparent',
  '&.inRange': {
    backgroundColor: theme.palette.primary.light,
  },
}));

/**
 * Day button styles, keyed by classes set in `CalendarDayCell`:
 * `selected`, `inRange`, `disabledReason-*`.
 */
export const CalendarDayButton = styled(PickersDay)(({ theme }) => {
  const { common, primary, action } = theme.palette;
  return {
    margin: 0,
    width: 40,
    height: 40,
    boxSizing: 'border-box',
    border: '2px solid transparent',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    color: DAY_FONT_COLOR.available,
    '&:hover': {
      borderRadius: '50%',
      backgroundColor: action.hover,
    },
    '&.Mui-focusVisible': {
      borderRadius: '50%',
      outline: 'none',
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&.inRange:not(.selected)': {
      borderRadius: 0,
      backgroundColor: primary.light,
      '&:hover': {
        borderRadius: 0,
        backgroundColor: primary.light,
      },
      '&.Mui-disabled': {
        opacity: 1,
        borderRadius: 0,
        backgroundColor: primary.light,
      },
    },
    '&.selected': {
      fontWeight: 'bold',
      backgroundColor: common.black,
      color: common.white,
      '&:hover': {
        borderRadius: '50%',
        backgroundColor: common.black,
      },
      '&.Mui-selected': {
        backgroundColor: common.black,
        color: common.white,
        '&:hover': {
          backgroundColor: common.black,
        },
      },
      '&.Mui-focusVisible': {
        backgroundColor: common.black,
      },
      '&.Mui-disabled': {
        opacity: 1,
        backgroundColor: common.black,
        color: common.white,
      },
    },
    [`&.disabledReason-${DATE_UNAVAILABILITY_REASON.NO_INVENTORY}:not(.selected)`]: {
      color: DAY_FONT_COLOR.noInventory,
      textDecoration: 'line-through',
    },
    [`&.disabledReason-${DATE_UNAVAILABILITY_REASON.MAX_LOS_EXCEEDED}:not(.selected)`]: {
      color: DAY_FONT_COLOR.outOfRange,
    },
    [RESTRICTED_REASON_CLASS_SELECTOR]: {
      color: DAY_FONT_COLOR.restricted,
    },
  };
});

/** Shared calendar chrome: hide header controls and remove gaps between day cells. */
export const calendarPickerChromeSx = {
  '& .MuiPickersCalendarHeader-switchViewButton': { display: 'none' },
  '& .MuiPickersCalendarHeader-leftArrowButton': { display: 'none' },
  '& .MuiPickersCalendarHeader-rightArrowButton': { display: 'none' },
  '& .MuiPickersCalendarHeader-labelContainer': {
    justifyContent: 'center',
    width: '100%',
    pointerEvents: 'none',
  },
  '& .MuiPickersDay-root': {
    margin: 0,
  },
  '& .PrivatePickersSlideTransition-root [role="row"]': {
    margin: 0,
  },
};
